import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Grid,
  GridItem,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Badge,
  Center,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { 
  MdEmail, 
  MdTrackChanges,
  MdBusiness,
  MdHail,
} from "react-icons/md";
import { 
  FaSlack, 
  FaUser, 
  FaHubspot, 
  FaSalesforce,
} from "react-icons/fa";
import { RiLightbulbFlashLine } from "react-icons/ri";
import { SiGooglesheets } from "react-icons/si";
import { ReactComponent as LystoLogo } from "assets/img/lysto_white.svg";

// Tag component similar to SparkCard's OverviewTag
// const IntegrationTag = ({ icon, label, colorScheme, customIconSrc }) => {
//   const bgColor = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`);
//   const textColor = useColorModeValue(`${colorScheme}.700`, `${colorScheme}.200`);
//   const iconBgColor = useColorModeValue(`${colorScheme}.100`, `${colorScheme}.800`);
//   const iconColor = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.300`);

//   return (
//     <Flex
//       borderRadius="full"
//       bg={bgColor}
//       px={3}
//       py={1}
//       alignItems="center"
//       borderWidth="1px"
//       borderColor={useColorModeValue(`${colorScheme}.200`, `${colorScheme}.700`)}
//     >
//       <Flex
//         alignItems="center"
//         justifyContent="center"
//         borderRadius="full"
//         bg={iconBgColor}
//         p={1}
//         mr={2}
//         boxSize="20px"
//       >
//         {customIconSrc ? (
//           <Image src={customIconSrc} width="12px" height="12px" />
//         ) : (
//           <Icon as={icon} color={iconColor} boxSize={3} />
//         )}
//       </Flex>
//       <Text fontSize="xs" fontWeight="medium" color={textColor}>
//         {label}
//       </Text>
//     </Flex>
//   );
// };

// Lysto logo component
const LystoIcon = (props) => (
  <Box {...props} display="flex" alignItems="center" justifyContent="center">
    <LystoLogo width="16px" height="16px" />
  </Box>
);

// Modified DataFlowAnimation component to visualize data conversion
const DataFlowAnimation = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
  
  // State for mobile carousel
  const [mobileSourceIndex, setMobileSourceIndex] = useState(0);
  const [mobileResultIndex, setMobileResultIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  // Add state for tracking visibility
  const [isVisible, setIsVisible] = useState(false);
  
  // Create a ref for the animation container
  const animationRef = useRef(null);
  
  // Update mobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Set initial state
    handleResize();
    
    // Add resize listener
    window.addEventListener('resize', handleResize);
    
    // Auto-advance carousels on mobile
    let sourceInterval;
    let resultInterval;
    
    if (isMobile && isVisible) {
      sourceInterval = setInterval(() => {
        setMobileSourceIndex(prev => (prev + 1) % dataSources.length);
      }, 4000);
      
      resultInterval = setInterval(() => {
        setMobileResultIndex(prev => (prev + 1) % 3);
      }, 5000);
    }
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      clearInterval(sourceInterval);
      clearInterval(resultInterval);
    };
  }, [isMobile, isVisible]);
  
  // Set up intersection observer
  useEffect(() => {
    const options = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.1 // trigger when at least 10% of the element is visible
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        setIsVisible(entry.isIntersecting);
      });
    }, options);
    
    if (animationRef.current) {
      observer.observe(animationRef.current);
    }
    
    return () => {
      if (animationRef.current) {
        observer.unobserve(animationRef.current);
      }
    };
  }, []);
  
  // Animation keyframes
  const floatIn = keyframes`
    0% { opacity: 0; transform: translateY(20px); }
    100% { opacity: 1; transform: translateY(0); }
  `;
  
  const pulse = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  `;
  
  const dataSources = [
    { 
      icon: MdEmail, 
      title: "Email", 
      color: "blue", 
      content: [
        "Subject: Partnership Inquiry",
        "From: kier.eagan@lumonindustries.com",
        "To: sales@yourcompany.com",
        "Body: Hi team, I'm interested in exploring a partnership with your biotech solutions. Lumon Industries has been at the forefront of biotechnology innovation for years, and we believe your products would integrate well with our platform."
      ],
      delay: "0s"
    },
    { 
      icon: FaSlack, 
      title: "Slack", 
      color: "teal", 
      content: [
        "#sales-team",
        "Alex: Just finished call with Lumon Industries - very positive!",
        "Maria: Did they mention budget?",
        "Alex: Yes, they have $175k allocated for Q4",
        "Ricky: Great! I'll schedule follow-up with their CTO"
      ],
      delay: "0.2s"
    },
    { 
      icon: SiGooglesheets, 
      title: "Google Sheets", 
      color: "green", 
      content: [
        "Company | Contact | Status",
        "Acme Inc | J. Smith | New Lead",
        "Lumon Industries | K. Eagan | In Discussion",
        "GlobalFirm | M. Davis | Contract Sent"
      ],
      delay: "0.4s"
    },
    { 
      icon: FaHubspot, 
      title: "HubSpot", 
      color: "orange", 
      content: [
        "Contact: Helena Eagan",
        "Company: Lumon Industries",
        "Last Contact: 3 days ago",
        "Status: Qualified Lead"
      ],
      delay: "0.6s"
    },
    { 
      icon: FaSalesforce, 
      title: "Salesforce", 
      color: "blue", 
      content: [
        "Opportunity: Biotech Partnership",
        "Amount: $175,000",
        "Stage: Negotiation",
        "Close Date: Next Month"
      ],
      delay: "0.8s"
    },
  ];
  
  // Function to create a tag similar to the existing Spark cards
  const Tag = ({ icon, label, colorScheme }) => {
    const bgColor = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`);
    const textColor = useColorModeValue(`${colorScheme}.700`, `${colorScheme}.200`);
    const iconBgColor = useColorModeValue(`${colorScheme}.100`, `${colorScheme}.800`);
    const iconColor = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.300`);

    return (
      <Flex
        borderRadius="full"
        bg={bgColor}
        px={3}
        py={1}
        alignItems="center"
        borderWidth="1px"
        borderColor={useColorModeValue(`${colorScheme}.200`, `${colorScheme}.700`)}
      >
        <Box
          borderRadius="full"
          bg={iconBgColor}
          p={1}
          mr={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon as={icon} color={iconColor} boxSize={3} />
        </Box>
        <Text fontSize="sm" fontWeight="medium" color={textColor}>
          {label}
        </Text>
      </Flex>
    );
  };
  
  // Insert local declaration for AccountIcon in DataFlowAnimation
  const AccountIcon = MdBusiness;

  const [containerWidth, setContainerWidth] = useState(1100);
  const containerRef = useRef(null);

  useEffect(() => {
    function updateWidth() {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    }
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  return (
    <Box
      ref={(el) => {
        containerRef.current = el;
        animationRef.current = el;
      }}
      mt={6}
      mb={12}
      position="relative"
      maxW="1100px"
      mx="auto"
      className={`data-flow-container ${isVisible ? 'animation-active' : 'animation-paused'}`}
    >
      {/* Data source cards */}
      <Grid
        templateColumns={{ base: "1fr", md: "repeat(5, 1fr)" }}
        gap={{ base: 5, md: 4 }}
      >
        {dataSources.map((source, index) => (
          (!isMobile || index === mobileSourceIndex) && (
          <GridItem key={index}>
            <Box
              bg={bgColor}
              borderRadius="md"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="sm"
              height={{ base: "auto", md: "180px" }}
              position="relative"
              animation={`${floatIn} 0.6s ease-out forwards`}
              style={{ animationDelay: source.delay }}
              opacity={0}
              overflow="hidden"
              className="source-card"
              zIndex={1}
            >
              {/* Source header */}
              <Box
                bg={`${source.color}.50`}
                borderBottom="1px"
                borderColor={`${source.color}.100`}
                p={2.5}
              >
                <Flex align="center">
                  <Center
                    bg={`${source.color}.100`}
                    color={`${source.color}.600`}
                    borderRadius="md"
                    boxSize="32px"
                    mr={2}
                  >
                    <Icon as={source.icon} boxSize={4} />
                  </Center>
                  <Text fontWeight="semibold" fontSize="sm" color={`${source.color}.700`}>
                    {source.title}
                  </Text>
                </Flex>
              </Box>
              
              {/* Source content showing raw data */}
              <Box p={3} fontSize="xs" fontFamily="mono">
                <VStack align="start" spacing={2}>
                  {source.content.map((line, i) => (
                    <Text key={i} color={secondaryTextColor} noOfLines={1}>
                      {line}
                    </Text>
                  ))}
                </VStack>
              </Box>
            </Box>
          </GridItem>
          )
        ))}
      </Grid>
      
      {/* Mobile indicators for source carousel */}
      {isMobile && (
        <>
        <Flex justify="center" mt={4} mb={2} display="none">
          {dataSources.map((_, index) => (
            <Box
              key={index}
              w="8px"
              h="8px"
              borderRadius="full"
              bg={index === mobileSourceIndex ? "blue.500" : "gray.200"}
              mx={1}
              cursor="pointer"
              onClick={() => setMobileSourceIndex(index)}
            />
          ))}
        </Flex>
        </>
      )}
      
      {/* Processing center with a data stream visual metaphor */}
      <Box
        position="relative"
        my={{ base: 2, md: 5 }}
        height={{ base: "120px", md: "120px" }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        {/* UPDATED: Connector lines from each card's dot to the central processor - using rounded corners */}
        {!isMobile && (
          <svg
            style={{ 
              position: 'absolute', 
              top: '-35px',
              left: 0, 
              width: '100%', 
              height: 'calc(100% + 10px)',
              pointerEvents: 'none',
              overflow: 'visible',
              zIndex: 0
            }}
            viewBox={`0 0 ${containerWidth} 190`}
            preserveAspectRatio="none"
          >
            {dataSources.map((source, index) => {
              const totalItems = dataSources.length;
              const itemWidth = containerWidth / totalItems;
              const sourceX = (index + 0.5) * itemWidth;
              const logoX = containerWidth / 2;
              const verticalDropDistance = 60; // Vertical drop before turning
              const cornerRadius = 15; // Radius for rounded corners
              
              const colorMap = { 
                blue: "#4299E1", 
                teal: "#38B2AC", 
                green: "#48BB78", 
                orange: "#ED8936" 
              };
              const strokeColor = colorMap[source.color] || "#4299E1";
              
              return (
                <g key={`connector-${index}`}>
                  {/* Vertical line down from source card */}
                  <line
                    x1={sourceX}
                    y1={0}
                    x2={sourceX}
                    y2={verticalDropDistance - cornerRadius}
                    stroke={strokeColor}
                    strokeWidth="3"
                    strokeDasharray="5, 5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`connector-line animated-line line-delay-${index % 5}`}
                    shapeRendering="geometricPrecision"
                  />
                  
                  {/* First curve */}
                  {sourceX < logoX ? (
                    <path
                      d={`M ${sourceX} ${verticalDropDistance - cornerRadius} 
                          Q ${sourceX} ${verticalDropDistance}, ${sourceX + cornerRadius} ${verticalDropDistance}`}
                      stroke={strokeColor}
                      strokeWidth="3"
                      fill="none"
                      strokeDasharray="5, 5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`connector-line animated-line line-delay-${index % 5}`}
                      shapeRendering="geometricPrecision"
                    />
                  ) : sourceX > logoX ? (
                    <path
                      d={`M ${sourceX} ${verticalDropDistance - cornerRadius} 
                          Q ${sourceX} ${verticalDropDistance}, ${sourceX - cornerRadius} ${verticalDropDistance}`}
                      stroke={strokeColor}
                      strokeWidth="3"
                      fill="none"
                      strokeDasharray="5, 5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`connector-line animated-line line-delay-${index % 5}`}
                      shapeRendering="geometricPrecision"
                    />
                  ) : null}
                  
                  {/* Horizontal line */}
                  {sourceX !== logoX && (
                    <line
                      x1={sourceX < logoX ? sourceX + cornerRadius : sourceX - cornerRadius}
                      y1={verticalDropDistance}
                      x2={sourceX < logoX ? logoX - cornerRadius : logoX + cornerRadius}
                      y2={verticalDropDistance}
                      stroke={strokeColor}
                      strokeWidth="3"
                      strokeDasharray="5, 5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`connector-line animated-line line-delay-${index % 5}`}
                      shapeRendering="geometricPrecision"
                    />
                  )}
                  
                  {/* Second curve */}
                  {sourceX !== logoX && (
                    <path
                      d={`M ${sourceX < logoX ? logoX - cornerRadius : logoX + cornerRadius} ${verticalDropDistance} 
                          Q ${logoX} ${verticalDropDistance}, ${logoX} ${verticalDropDistance + cornerRadius}`}
                      stroke={strokeColor}
                      strokeWidth="3"
                      fill="none"
                      strokeDasharray="5, 5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`connector-line animated-line line-delay-${index % 5}`}
                      shapeRendering="geometricPrecision"
                    />
                  )}
                  
                  {/* Vertical line to logo */}
                  <line
                    x1={logoX}
                    y1={sourceX !== logoX ? verticalDropDistance + cornerRadius : verticalDropDistance}
                    x2={logoX}
                    y2={190}
                    stroke={strokeColor}
                    strokeWidth="3"
                    strokeDasharray="5, 5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`connector-line animated-line line-delay-${index % 5}`}
                    shapeRendering="geometricPrecision"
                  />
                </g>
              );
            })}
          </svg>
        )}
        
        {/* Simplified mobile connector - single unified line */}
        {isMobile && (
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 0
            }}
            viewBox={`0 0 ${containerWidth} ${containerRef.current ? containerRef.current.offsetHeight : 500}`}
            preserveAspectRatio="none"
          >
            <line
              x1={containerWidth / 2}
              y1="0"
              x2={containerWidth / 2}
              y2={containerRef.current ? containerRef.current.offsetHeight : 500}
              stroke="#4299E1"
              strokeWidth="3"
              strokeDasharray="5, 5"
              strokeLinecap="round"
              className="connector-line animated-line"
            />
          </svg>
        )}
        
        {/* Central Lysto Import AI processor */}
        <Flex
          position="relative"
          bg="blue.500" 
          animation={`${pulse} 3s infinite ease-in-out`}
          borderRadius="16px"
          p={4}
          boxShadow="lg"
          width="56px"
          height="56px"
          zIndex={1}
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          border="2px solid"
          borderColor="blue.300"
          overflow="hidden"
          _after={{
            content: '""',
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            borderRadius: '10px',
            background: 'transparent',
            opacity: 0.3,
          }}
        >
          <LystoIcon boxSize={4} style={{ transform: 'scale(1.5)' }} />
        </Flex>
      </Box>
      
      {/* UPDATED: Connector lines from the Import AI card to the result cards - with similar pattern to top connections */}
      {!isMobile && (
        <svg
          style={{ 
            position: 'absolute', 
            top: '230px',
            left: 0, 
            width: '100%', 
            height: '120px',
            pointerEvents: 'none',
            overflow: 'visible',
            zIndex: 0
          }}
          viewBox={`0 0 ${containerWidth} 200`} 
          preserveAspectRatio="none"
        >
          {/* First draw the line from logo down to the junction point */}
          <line
            x1={containerWidth / 2}
            y1={0}
            x2={containerWidth / 2}
            y2={40}
            stroke="#4299E1"
            strokeWidth="3"
            strokeDasharray="5, 5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="connector-line animated-line"
            shapeRendering="geometricPrecision"
          />
          
          {/* Draw 3 lines from junction to each result card */}
          {[0, 1, 2].map((index) => {
            const itemWidthBottom = containerWidth / 3;
            const resultX = (index + 0.5) * itemWidthBottom;
            const logoX = containerWidth / 2;
            const verticalDistanceBeforeTurn = 40; // Junction point
            const cornerRadius = 15; // Radius for rounded corners
            
            // Color map to match result card types
            const colorMap = ['#4299E1', '#3182CE', '#2B6CB0']; // Blues with different shades
            const strokeColor = colorMap[index];
            
            return (
              <g key={`result-connector-${index}`}>
                {/* Only create curved path if not the middle card */}
                {logoX !== resultX && (
                  <>
                    {/* First curve at junction */}
                    <path
                      d={`M ${logoX} ${verticalDistanceBeforeTurn} 
                          Q ${logoX} ${verticalDistanceBeforeTurn}, ${logoX < resultX ? logoX + cornerRadius : logoX - cornerRadius} ${verticalDistanceBeforeTurn}`}
                      stroke={strokeColor}
                      strokeWidth="3"
                      fill="none"
                      strokeDasharray="5, 5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`connector-line animated-line line-delay-${index}`}
                      shapeRendering="geometricPrecision"
                    />
                    
                    {/* Horizontal line */}
                    <line
                      x1={logoX < resultX ? logoX + cornerRadius : logoX - cornerRadius}
                      y1={verticalDistanceBeforeTurn}
                      x2={logoX < resultX ? resultX - cornerRadius : resultX + cornerRadius}
                      y2={verticalDistanceBeforeTurn}
                      stroke={strokeColor}
                      strokeWidth="3"
                      strokeDasharray="5, 5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`connector-line animated-line line-delay-${index}`}
                      shapeRendering="geometricPrecision"
                    />
                    
                    {/* Second curve */}
                    <path
                      d={`M ${logoX < resultX ? resultX - cornerRadius : resultX + cornerRadius} ${verticalDistanceBeforeTurn} 
                          Q ${resultX} ${verticalDistanceBeforeTurn}, ${resultX} ${verticalDistanceBeforeTurn + cornerRadius}`}
                      stroke={strokeColor}
                      strokeWidth="3"
                      fill="none"
                      strokeDasharray="5, 5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`connector-line animated-line line-delay-${index}`}
                      shapeRendering="geometricPrecision"
                    />
                  </>
                )}
                
                {/* Vertical line to result card */}
                <line
                  x1={resultX}
                  y1={logoX !== resultX ? verticalDistanceBeforeTurn + cornerRadius : verticalDistanceBeforeTurn}
                  x2={resultX}
                  y2={200}
                  stroke={strokeColor}
                  strokeWidth="3"
                  strokeDasharray="5, 5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`connector-line animated-line line-delay-${index}`}
                  shapeRendering="geometricPrecision"
                />
              </g>
            );
          })}
        </svg>
      )}
      
      {/* Result cards - updated to match Spark card style */}
      <Grid
        templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
        gap={5}
        mt={3}
      >
        {[
          {
            title: "Kier Eagan",
            subtitle: "Primary contact at Lumon Industries",
            type: "contact",
            delay: "0.6s",
            confidence: 0.90,
            date: "Today",
            source: "AUTO",
            summary: "Kier Eagan is the primary decision maker at Lumon Industries who initiated the partnership inquiry. Multiple communication channels show high engagement level.",
            tags: [
              { icon: MdEmail, label: "From Email", colorScheme: "blue" },
              { icon: SiGooglesheets, label: "From Sheets", colorScheme: "green" }
            ]
          },
          {
            title: "Lumon Deal",
            subtitle: "Enterprise biotechnology deal",
            type: "deal",
            delay: "0.8s",
            confidence: 0.85,
            date: "Yesterday",
            source: "AUTO",
            summary: "Enterprise deal with Lumon Industries for biotechnology solutions. Budget confirmed at $175K from multiple sources. Currently in negotiation stage.",
            tags: [
              { icon: FaUser, label: "3 contacts detected", colorScheme: "purple" },
              { icon: FaSlack, label: "From Slack", colorScheme: "teal" },
              { icon: FaSalesforce, label: "From Salesforce", colorScheme: "blue" }
            ]
          },
          {
            title: "Lumon Industries",
            subtitle: "Corporate biotech account",
            type: "account",
            delay: "1s",
            confidence: 0.78,
            date: "Last Week",
            source: "AUTO",
            summary: "Biotechnology firm with active communication across multiple channels. Key contacts include Kier and Helena Eagan. Engagement level is high with ongoing partnership discussions.",
            tags: [
              { icon: MdEmail, label: "From Email", colorScheme: "blue" },
              { icon: FaUser, label: "3 contacts", colorScheme: "purple" },
              { icon: FaHubspot, label: "From HubSpot", colorScheme: "orange" }
            ]
          }
        ].map((result, index) => (
          (!isMobile || index === mobileResultIndex) && (
          <GridItem key={index}>
            <Box
              bg={bgColor}
              p={5}
              borderRadius="md"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="sm"
              animation={`${floatIn} 0.6s ease-out forwards`}
              style={{ animationDelay: result.delay }}
              opacity={0}
              height="100%"
              position="relative"
              transition="all 0.2s"
              className="result-card"
              zIndex={1}
              _hover={{
                borderColor: "gray.300",
                boxShadow: "md",
                transform: "translateY(-2px)",
              }}
            >
              <VStack spacing={3} align="stretch">
                <Flex justify="space-between" align="center">
                  <Flex align="center">
                    <Center
                      bg="blue.50"
                      borderRadius="md"
                      mr={3}
                      borderWidth="1px"
                      borderColor="blue.200"
                      boxSize="36px"
                    >
                      <Icon as={result.type === "account" ? AccountIcon : result.type === "deal" ? MdTrackChanges : MdHail} color="blue.500" boxSize={5} />
                    </Center>
                    <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                      {result.title}
                    </Text>
                  </Flex>
                  <Badge colorScheme="blue" fontSize="sm" borderRadius="md">
                    {result.type === "contact" ? "Contact" : result.type === "deal" ? "Deal" : "Account"}
                  </Badge>
                </Flex>

                <Text fontSize="md" color={secondaryTextColor}>
                  {result.subtitle}
                </Text>

                <HStack spacing={2} flexWrap="wrap">
                  {result.tags.map((tag, tagIndex) => (
                    <Tag key={tagIndex} icon={tag.icon} label={tag.label} colorScheme={tag.colorScheme} />
                  ))}
                </HStack>

                <Box mt={1}>
                  <Text fontSize="sm" fontWeight="semibold" color={textColor} mb={2}>
                    AI Summary
                  </Text>
                  <Box
                    bg={useColorModeValue("gray.50", "gray.700")}
                    p={3}
                    borderRadius="md"
                    position="relative"
                  >
                    <Text fontSize="sm" color={secondaryTextColor}>
                      {result.summary}
                    </Text>
                  </Box>
                </Box>
              </VStack>
            </Box>
          </GridItem>
          )
        ))}
      </Grid>
      
      {/* Mobile indicators for result carousel */}
      {isMobile && (
        <Flex justify="center" mt={2} mb={1} display="none">
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              w="8px"
              h="8px"
              borderRadius="full"
              bg={index === mobileResultIndex ? "blue.500" : "gray.200"}
              mx={1}
              cursor="pointer"
              onClick={() => setMobileResultIndex(index)}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};

const LandingSparkSection = () => {
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const textColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
  // Track screen size for responsive card display
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Modified style with animation control based on visibility
  useEffect(() => {
    // Create a style tag to add custom CSS rules
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `
      .card-container {
        /* Allow for variable collapsed heights based on content */
        min-height: 620px;
        height: auto;
        max-height: ${windowWidth < 768 ? '750px' : '720px'};
        overflow: hidden;
        transition: all 0.9s cubic-bezier(0.33, 1, 0.68, 1),
                   box-shadow 0.2s ease,
                   transform 0.2s ease;
        will-change: transform;
        position: relative;
        display: flex;
        flex-direction: column;
      }
      
      /* Updated animated connector lines with downward flow */
      @keyframes flowDownAnimation {
        0% { stroke-dashoffset: 0; }
        100% { stroke-dashoffset: -20; }
      }
      
      @keyframes flowVerticalAnimation {
        0% { stroke-dashoffset: 0; }
        100% { stroke-dashoffset: -20; }
      }
      
      .connector-line {
        stroke-width: 3;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-dasharray: 5, 5;
        filter: drop-shadow(0 0 1px rgba(66, 153, 225, 0.5));
        vector-effect: non-scaling-stroke;
      }
      
      /* Animations - only run when container is visible */
      .animation-active .animated-line {
        animation: flowDownAnimation 1.5s linear infinite;
      }
      
      .animation-paused .animated-line {
        animation: none !important;
      }
      
      .animation-active .line-delay-1 {
        animation-delay: 0.1s;
      }
      
      .animation-active .line-delay-2 {
        animation-delay: 0.2s;
      }
      
      .animation-active .line-delay-3 {
        animation-delay: 0.3s;
      }
      
      .animation-active .line-delay-4 {
        animation-delay: 0.4s;
      }
      
      /* Mobile styling adjustments */
      @media (max-width: 768px) {
        /* Ensure mobile connector lines have correct dashed appearance */
        .connector-line {
          stroke-dasharray: 5, 5 !important;
        }
        
        /* Keep same animation for mobile connector but only when visible */
        .animation-active .animated-line {
          animation: flowDownAnimation 1.5s linear infinite;
        }
        
        /* Hide the extra connector in mobile view */
        .mobile-vertical-connector {
          display: none;
        }
        
        /* Adjust spacing for mobile when lines are hidden */
        .data-flow-container {
          margin-top: 15px;
          margin-bottom: 15px;
        }
        
        /* Reduce vertical spacing in mobile view */
        .data-flow-container > div + div {
          margin-top: 5px;
        }
        
        /* Make source and result cards more compact in mobile */
        .source-card, .result-card {
          margin-bottom: 0;
        }
        
        /* Reduce height of processing center on mobile */
        .data-flow-container .chakra-box {
          margin-top: 0;
          margin-bottom: 0;
        }
      }
    `;
    document.head.appendChild(styleTag);
    
    // Clean up function
    return () => {
      document.head.removeChild(styleTag);
    };
  }, [windowWidth]);

  return (
    <Box bg={bgColor} py={16} id="spark-section">
      <Container maxW="1400px" px={8}>
        <VStack spacing={12} align="stretch">
          {/* Headline Section - Updated with new text */}
          <VStack spacing={4} align="center" textAlign="center">
            <Flex
              bg="yellow.50"
              borderRadius="full"
              p={2}
              alignItems="center"
              justifyContent="center"
              boxSize="60px"
              mb={1}
            >
              <Icon as={RiLightbulbFlashLine} color="yellow.500" boxSize={8} />
            </Flex>
            
            <Heading
              as="h2"
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="bold"
              color={textColor}
              mb={1}
            >
              Your GTM Data, Finally in Sync
            </Heading>
            
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              maxW="800px"
              color={secondaryTextColor}
            >
              Lysto structures data from your communication platforms, sales tools, accounting stack, and more to generate a complete revenue intelligence system automatically. No manual data entry required. 
            </Text>
          </VStack>

          {/* New Data Flow Animation component */}
          <DataFlowAnimation />
        </VStack>
      </Container>
    </Box>
  );
};

export default LandingSparkSection; 